import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Car,
  AlertCircle,
  LogIn,
  LogOut,
  Ban,
  ShieldOff,
  Camera,
  MapPin,
  Grid3x3,
  ShieldAlert,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  getAllVehicles,
  deleteVehicle,
  logVehicleEntry,
  logVehicleExit,
  markVehicleRestricted,
  clearVehicleRestriction,
  checkRestrictedVehicleAtGate,
  anprReadPlate,
  getParkingSlots,
  createParkingSlot,
  assignParkingSlot,
  releaseParkingSlot,
  getBoundaryBreaches,
  createBoundaryBreach,
  ParkingSlot,
  BoundaryBreach,
} from "../../../service/securityService";
import {
  Vehicle,
  SecuritySearchFilters,
  PaginationParams,
} from "../../../types/security_types";
import VehicleForm from "./VehicleForm";
import { normalizeVehiclePlate } from "./parkingPlateUtils";

type ParkingTab = "vehicles" | "logs" | "slots" | "gate" | "breaches";

const ParkingManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ParkingTab>("vehicles");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [ownerTypeFilter, setOwnerTypeFilter] = useState<string>("ALL");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 100,
  });

  const [slots, setSlots] = useState<ParkingSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [breaches, setBreaches] = useState<BoundaryBreach[]>([]);
  const [loadingBreaches, setLoadingBreaches] = useState(false);

  const [logModal, setLogModal] = useState<"entry" | "exit" | null>(null);
  const [logVehicle, setLogVehicle] = useState<Vehicle | null>(null);
  const [gateInput, setGateInput] = useState("");

  const [slotModal, setSlotModal] = useState(false);
  const [slotForm, setSlotForm] = useState({
    slotNumber: "",
    zone: "",
    parkingLevel: "",
  });
  const [assignModal, setAssignModal] = useState<ParkingSlot | null>(null);
  const [assignVehicleId, setAssignVehicleId] = useState("");

  const [breachModal, setBreachModal] = useState(false);
  const [breachForm, setBreachForm] = useState({
    breachType: "Perimeter crossing",
    location: "",
    description: "",
    severity: "High",
  });

  const [anprPlate, setAnprPlate] = useState("");
  const [anprGate, setAnprGate] = useState("");
  const [anprLocation, setAnprLocation] = useState("");
  const [anprAutoLog, setAnprAutoLog] = useState(true);
  const [anprBusy, setAnprBusy] = useState(false);

  const [checkPlate, setCheckPlate] = useState("");
  const [checkLocation, setCheckLocation] = useState("");
  const [checkBusy, setCheckBusy] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, [
    searchQuery,
    selectedRegion,
    selectedPremises,
    ownerTypeFilter,
    pagination,
  ]);

  useEffect(() => {
    if (activeTab === "slots") fetchSlots();
    if (activeTab === "breaches") fetchBreaches();
  }, [activeTab]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;

      const response = await getAllVehicles(filters, pagination);
      setVehicles(response.data || []);
    } catch (error: any) {
      console.error("Error fetching vehicles:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to load vehicles",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    try {
      setLoadingSlots(true);
      const res = await getParkingSlots({ page: 1, limit: 200 });
      setSlots(res.data || []);
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Failed to load parking slots",
      );
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchBreaches = async () => {
    try {
      setLoadingBreaches(true);
      const res = await getBoundaryBreaches({ page: 1, limit: 100 });
      setBreaches(res.data || []);
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Failed to load boundary breaches",
      );
    } finally {
      setLoadingBreaches(false);
    }
  };

  const handleCreate = () => {
    setSelectedVehicle(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (vehicle: Vehicle) => {
    if (!window.confirm(`Delete vehicle "${vehicle.vehicleNumber}"?`)) {
      return;
    }
    try {
      const id = vehicle._id || vehicle.id;
      if (!id) return;
      await deleteVehicle(id);
      toast.success("Vehicle deleted");
      fetchVehicles();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to delete",
      );
    }
  };

  const handleFormSuccess = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedVehicle(null);
    fetchVehicles();
  };

  const openLogModal = (v: Vehicle, type: "entry" | "exit") => {
    setLogVehicle(v);
    setLogModal(type);
    setGateInput("");
  };

  const submitLog = async () => {
    const id = logVehicle?._id || logVehicle?.id;
    if (!id || !logModal) return;
    try {
      if (logModal === "entry") {
        await logVehicleEntry(id, {
          gateNumber: gateInput.trim() || undefined,
        });
        toast.success("Entry logged");
      } else {
        await logVehicleExit(id, { gateNumber: gateInput.trim() || undefined });
        toast.success("Exit logged");
      }
      setLogModal(null);
      setLogVehicle(null);
      fetchVehicles();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Failed to log",
      );
    }
  };

  const handleMarkRestricted = async (v: Vehicle) => {
    const reason = window.prompt("Restriction reason:", "") ?? "";
    if (reason === null) return;
    const id = v._id || v.id;
    if (!id) return;
    try {
      await markVehicleRestricted(id, { reason: reason || undefined });
      toast.success("Vehicle marked restricted");
      fetchVehicles();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message || e.response?.data?.resMessage || "Failed",
      );
    }
  };

  const handleClearRestriction = async (v: Vehicle) => {
    const id = v._id || v.id;
    if (!id) return;
    try {
      await clearVehicleRestriction(id);
      toast.success("Restriction cleared");
      fetchVehicles();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message || e.response?.data?.resMessage || "Failed",
      );
    }
  };

  const runAnpr = async () => {
    const raw = anprPlate.trim();
    if (!raw) {
      toast.error("Enter plate read from ANPR");
      return;
    }
    try {
      setAnprBusy(true);
      const res = await anprReadPlate({
        rawPlate: raw,
        gateNumber: anprGate.trim() || undefined,
        location: anprLocation.trim() || undefined,
        autoLogEntry: anprAutoLog,
      });
      if (!(res as any).success) {
        toast.error(String((res as any).message || "ANPR request failed"));
        return;
      }
      if (!(res as any).matched) {
        toast.warning(String((res as any).message || "Unknown vehicle"));
      } else {
        const allow = (res as any).allowEntry;
        if (allow) {
          toast.success(
            (res as any).entryLogged
              ? "Vehicle allowed — entry logged"
              : "Vehicle allowed",
          );
        } else {
          toast.error(
            "Entry denied — blacklisted, restricted, or outside validity (incident may be created)",
          );
        }
      }
      fetchVehicles();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "ANPR failed",
      );
    } finally {
      setAnprBusy(false);
    }
  };

  const runRestrictedCheck = async () => {
    const n = normalizeVehiclePlate(checkPlate);
    if (!n) {
      toast.error("Enter vehicle number");
      return;
    }
    try {
      setCheckBusy(true);
      const r = await checkRestrictedVehicleAtGate({
        vehicleNumber: n,
        location: checkLocation.trim() || undefined,
      });
      if (r.isRestricted || r.alert) {
        toast.warning(r.message || "Restricted vehicle alert");
      } else {
        toast.success(r.message || "Not restricted");
      }
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Check failed",
      );
    } finally {
      setCheckBusy(false);
    }
  };

  const submitSlot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slotForm.slotNumber.trim()) return;
    try {
      await createParkingSlot({
        slotNumber: slotForm.slotNumber.trim(),
        zone: slotForm.zone.trim() || undefined,
        parkingLevel: slotForm.parkingLevel.trim() || undefined,
      });
      toast.success("Slot created");
      setSlotModal(false);
      setSlotForm({ slotNumber: "", zone: "", parkingLevel: "" });
      fetchSlots();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message || e.response?.data?.resMessage || "Failed",
      );
    }
  };

  const submitAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignModal || !assignVehicleId) return;
    try {
      await assignParkingSlot(assignModal._id || assignModal.id || "", {
        vehicleId: assignVehicleId,
      });
      toast.success("Slot assigned");
      setAssignModal(null);
      setAssignVehicleId("");
      fetchSlots();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Assign failed",
      );
    }
  };

  const submitBreach = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createBoundaryBreach({
        breachType: breachForm.breachType,
        location: breachForm.location,
        description: breachForm.description,
        severity: breachForm.severity,
      });
      toast.success("Boundary breach recorded");
      setBreachModal(false);
      setBreachForm({
        breachType: "Perimeter crossing",
        location: "",
        description: "",
        severity: "High",
      });
      fetchBreaches();
    } catch (e: any) {
      toast.error(
        e.response?.data?.message || e.response?.data?.resMessage || "Failed",
      );
    }
  };

  const logRows = useMemo(() => {
    const rows: Array<{
      key: string;
      type: string;
      vehicleNumber: string;
      time: string;
      gate?: string;
      source?: string;
    }> = [];
    for (const v of vehicles) {
      const id = v._id || v.id || "";
      (v.entryLogs || []).forEach((e, i) =>
        rows.push({
          key: `e-${id}-${i}`,
          type: "Entry",
          vehicleNumber: v.vehicleNumber,
          time: e.entryTime,
          gate: e.gateNumber,
          source: e.source,
        }),
      );
      (v.exitLogs || []).forEach((e, i) =>
        rows.push({
          key: `x-${id}-${i}`,
          type: "Exit",
          vehicleNumber: v.vehicleNumber,
          time: e.exitTime,
          gate: e.gateNumber,
        }),
      );
    }
    return rows.sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );
  }, [vehicles]);

  const occupiedSlots = slots.filter((s) => s.status === "Occupied").length;

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex flex-wrap justify-between items-start gap-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Car className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Parking &amp; Perimeter Security
              </h1>
              <p className="text-gray-600 max-w-3xl">
                Entry/exit logs, ANPR gate simulation, slot management,
                restricted vehicle checks, and boundary breach recording —
                integrated with Security incidents.
              </p>
            </div>
          </div>
          {activeTab === "vehicles" && (
            <button
              type="button"
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600"
            >
              <Plus className="w-5 h-5" />
              Register vehicle
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {[
            { icon: LogIn, t: "Entry / exit logs", d: "Per gate & vehicle" },
            { icon: Camera, t: "ANPR / LPR", d: "Normalize & validate" },
            { icon: Grid3x3, t: "Parking slots", d: "Assign & release" },
            { icon: Ban, t: "Restricted alerts", d: "Gate check API" },
            {
              icon: ShieldAlert,
              t: "Boundary breaches",
              d: "Perimeter events",
            },
          ].map((x) => (
            <div
              key={x.t}
              className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm text-sm"
            >
              <x.icon className="w-5 h-5 text-[#0fa9a6] mb-1" />
              <p className="font-semibold text-gray-900">{x.t}</p>
              <p className="text-xs text-gray-500">{x.d}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 border-b">
          {(
            [
              ["vehicles", "Vehicles"],
              ["logs", "Entry / exit log"],
              ["slots", "Parking slots"],
              ["gate", "ANPR gate"],
              ["breaches", "Boundary breaches"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === id
                  ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">Registered vehicles</p>
          <p className="text-2xl font-bold text-gray-900">{vehicles.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">Restricted</p>
          <p className="text-2xl font-bold text-orange-600">
            {vehicles.filter((v) => v.isRestricted).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">Blacklisted</p>
          <p className="text-2xl font-bold text-red-600">
            {vehicles.filter((v) => v.blacklisted).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">Slots occupied</p>
          <p className="text-2xl font-bold text-[#0fa9a6]">{occupiedSlots}</p>
          <p className="text-xs text-gray-500">{slots.length} total slots</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">Log lines (page)</p>
          <p className="text-2xl font-bold text-slate-700">{logRows.length}</p>
        </div>
      </div>

      {activeTab === "vehicles" && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <input
                type="text"
                placeholder="Search plate or owner…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
            <select
              value={ownerTypeFilter}
              onChange={(e) => setOwnerTypeFilter(e.target.value)}
              className="min-w-[140px] px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="ALL">All owner types</option>
              <option value="Staff">Staff</option>
              <option value="Visitor">Visitor</option>
              <option value="Vendor">Vendor</option>
            </select>
          </div>
        </div>
      )}

      {activeTab === "vehicles" && (
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex justify-center p-8">
              <SpinLoader />
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center text-gray-500 p-12">
              <Car className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No vehicles match filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="py-3 px-3 font-semibold">Plate</th>
                    <th className="py-3 px-3 font-semibold">Owner</th>
                    <th className="py-3 px-3 font-semibold">Zone</th>
                    <th className="py-3 px-3 font-semibold">Validity</th>
                    <th className="py-3 px-3 font-semibold">Status</th>
                    <th className="py-3 px-3 font-semibold text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles
                    .filter(
                      (v) =>
                        ownerTypeFilter === "ALL" ||
                        v.ownerType === ownerTypeFilter,
                    )
                    .map((vehicle) => (
                      <tr
                        key={vehicle._id || vehicle.id}
                        className={`border-b hover:bg-gray-50 ${
                          vehicle.blacklisted ? "bg-red-50" : ""
                        }`}
                      >
                        <td className="py-2 px-3 font-medium">
                          {vehicle.vehicleNumber}
                        </td>
                        <td className="py-2 px-3">
                          <div>{vehicle.ownerName}</div>
                          <div className="text-xs text-gray-500">
                            {vehicle.ownerType}
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          {vehicle.parkingZone || "—"}
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {vehicle.validityStart && vehicle.validityEnd
                            ? `${new Date(
                                vehicle.validityStart,
                              ).toLocaleDateString()} – ${new Date(
                                vehicle.validityEnd,
                              ).toLocaleDateString()}`
                            : "—"}
                        </td>
                        <td className="py-2 px-3">
                          {vehicle.blacklisted ? (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">
                              Blacklisted
                            </span>
                          ) : vehicle.isRestricted ? (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-900">
                              Restricted
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                              OK
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap justify-center gap-1">
                            <button
                              type="button"
                              title="Log entry"
                              className="p-1.5 text-green-700 hover:bg-green-50 rounded"
                              onClick={() => openLogModal(vehicle, "entry")}
                            >
                              <LogIn className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Log exit"
                              className="p-1.5 text-slate-700 hover:bg-slate-100 rounded"
                              onClick={() => openLogModal(vehicle, "exit")}
                            >
                              <LogOut className="w-4 h-4" />
                            </button>
                            {!vehicle.isRestricted ? (
                              <button
                                type="button"
                                title="Mark restricted"
                                className="p-1.5 text-orange-700 hover:bg-orange-50 rounded"
                                onClick={() => handleMarkRestricted(vehicle)}
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                title="Clear restriction"
                                className="p-1.5 text-teal-700 hover:bg-teal-50 rounded"
                                onClick={() => handleClearRestriction(vehicle)}
                              >
                                <ShieldOff className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="p-1.5 text-[#0fa9a6] hover:bg-[#EAFBFA] rounded"
                              onClick={() => handleEdit(vehicle)}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              onClick={() => handleDelete(vehicle)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "logs" && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">
              Vehicle entry / exit (from registered vehicles on this page)
            </h3>
            <button
              type="button"
              onClick={() => fetchVehicles()}
              className="flex items-center gap-1 text-sm text-[#0fa9a6]"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
          {logRows.length === 0 ? (
            <p className="p-8 text-center text-gray-500">
              No logs yet. Use Entry/Exit on the Vehicles tab or ANPR auto-log.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left">Type</th>
                    <th className="py-2 px-3 text-left">Plate</th>
                    <th className="py-2 px-3 text-left">Time</th>
                    <th className="py-2 px-3 text-left">Gate</th>
                    {/* <th className="py-2 px-3 text-left">Source</th> */}
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((r) => (
                    <tr key={r.key} className="border-b">
                      <td className="py-2 px-3">{r.type}</td>
                      <td className="py-2 px-3 font-medium">
                        {r.vehicleNumber}
                      </td>
                      <td className="py-2 px-3">
                        {new Date(r.time).toLocaleString()}
                      </td>
                      <td className="py-2 px-3">{r.gate || "—"}</td>
                      {/* <td className="py-2 px-3">{r.source || "—"}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "slots" && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b flex flex-wrap justify-between gap-2">
            <h3 className="font-semibold">Parking slots</h3>
            <button
              type="button"
              onClick={() => setSlotModal(true)}
              className="px-4 py-2 bg-[#0fa9a6] text-white rounded-full text-sm"
            >
              Add slot
            </button>
          </div>
          {loadingSlots ? (
            <div className="flex justify-center p-8">
              <SpinLoader />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left">Slot</th>
                    <th className="py-2 px-3 text-left">Zone</th>
                    <th className="py-2 px-3 text-left">Level</th>
                    <th className="py-2 px-3 text-left">Status</th>
                    <th className="py-2 px-3 text-left">Vehicle ID</th>
                    <th className="py-2 px-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((s) => (
                    <tr key={s._id || s.id} className="border-b">
                      <td className="py-2 px-3 font-medium">{s.slotNumber}</td>
                      <td className="py-2 px-3">{s.zone || "—"}</td>
                      <td className="py-2 px-3">{s.parkingLevel || "—"}</td>
                      <td className="py-2 px-3">{s.status}</td>
                      <td className="py-2 px-3 text-xs">
                        {s.currentVehicleId || "—"}
                      </td>
                      <td className="py-2 px-3 text-center space-x-1">
                        {(s.status === "Available" ||
                          s.status === "Reserved") && (
                          <button
                            type="button"
                            className="text-xs text-[#0fa9a6] underline"
                            onClick={() => setAssignModal(s)}
                          >
                            Assign
                          </button>
                        )}
                        {s.status === "Occupied" && (
                          <button
                            type="button"
                            className="text-xs text-orange-600 underline"
                            onClick={async () => {
                              try {
                                await releaseParkingSlot(String(s._id || s.id));
                                toast.success("Released");
                                fetchSlots();
                              } catch (e: any) {
                                toast.error(
                                  e.response?.data?.message || "Failed",
                                );
                              }
                            }}
                          >
                            Release
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "gate" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-5 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#0fa9a6]" />
              ANPR / LPR read
            </h3>
            <p className="text-sm text-gray-600">
              Simulates a camera read: normalizes the plate, matches the
              registry, checks validity and restrictions, optionally logs entry
              when allowed.
            </p>
            <input
              className="w-full border rounded-md p-2"
              placeholder="Raw plate (e.g. KA-01-AB-1234)"
              value={anprPlate}
              onChange={(e) => setAnprPlate(e.target.value)}
            />
            <input
              className="w-full border rounded-md p-2"
              placeholder="Gate / lane ID"
              value={anprGate}
              onChange={(e) => setAnprGate(e.target.value)}
            />
            <input
              className="w-full border rounded-md p-2"
              placeholder="Location label"
              value={anprLocation}
              onChange={(e) => setAnprLocation(e.target.value)}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={anprAutoLog}
                onChange={(e) => setAnprAutoLog(e.target.checked)}
              />
              Auto-log entry when allowed
            </label>
            <button
              type="button"
              disabled={anprBusy}
              onClick={runAnpr}
              className="w-full py-2 bg-[#0fa9a6] text-white rounded-lg disabled:opacity-50"
            >
              {anprBusy ? "Processing…" : "Process ANPR read"}
            </button>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-5 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Restricted vehicle check
            </h3>
            <p className="text-sm text-gray-600">
              Calls the gate API: if the plate is flagged restricted, a Security
              incident is created (backend).
            </p>
            <input
              className="w-full border rounded-md p-2"
              placeholder="Vehicle number"
              value={checkPlate}
              onChange={(e) => setCheckPlate(e.target.value.toUpperCase())}
            />
            <input
              className="w-full border rounded-md p-2"
              placeholder="Location (optional)"
              value={checkLocation}
              onChange={(e) => setCheckLocation(e.target.value)}
            />
            <button
              type="button"
              disabled={checkBusy}
              onClick={runRestrictedCheck}
              className="w-full py-2 border-2 border-orange-500 text-orange-700 rounded-lg disabled:opacity-50"
            >
              {checkBusy ? "Checking…" : "Run restricted check"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "breaches" && (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b flex justify-between">
            <h3 className="font-semibold">Boundary breaches</h3>
            <button
              type="button"
              onClick={() => setBreachModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-full text-sm"
            >
              Report breach
            </button>
          </div>
          {loadingBreaches ? (
            <div className="flex justify-center p-8">
              <SpinLoader />
            </div>
          ) : breaches.length === 0 ? (
            <p className="p-8 text-center text-gray-500">
              No breaches recorded.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left">#</th>
                    <th className="py-2 px-3 text-left">Type</th>
                    <th className="py-2 px-3 text-left">Location</th>
                    <th className="py-2 px-3 text-left">Detected</th>
                    <th className="py-2 px-3 text-left">Severity</th>
                    <th className="py-2 px-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {breaches.map((b) => (
                    <tr key={b._id || b.id} className="border-b">
                      <td className="py-2 px-3">{b.breachNumber}</td>
                      <td className="py-2 px-3">{b.breachType}</td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {b.location}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {b.detectedAt
                          ? new Date(b.detectedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="py-2 px-3">{b.severity}</td>
                      <td className="py-2 px-3">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={!!logModal}
        onClose={() => {
          setLogModal(null);
          setLogVehicle(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg"
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">
            Log {logModal === "entry" ? "entry" : "exit"} —{" "}
            {logVehicle?.vehicleNumber}
          </h2>
          <label className="text-sm text-gray-700">Gate / lane</label>
          <input
            className="w-full border rounded-md p-2 mt-1 mb-4"
            value={gateInput}
            onChange={(e) => setGateInput(e.target.value)}
            placeholder="e.g. North-1"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-full bg-gray-200"
              onClick={() => setLogModal(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-full bg-[#0fa9a6] text-white"
              onClick={submitLog}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={slotModal}
        onClose={() => setSlotModal(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg"
      >
        <form onSubmit={submitSlot} className="p-6 space-y-3">
          <h2 className="text-lg font-semibold">New parking slot</h2>
          <input
            required
            className="w-full border rounded-md p-2"
            placeholder="Slot number *"
            value={slotForm.slotNumber}
            onChange={(e) =>
              setSlotForm({ ...slotForm, slotNumber: e.target.value })
            }
          />
          <input
            className="w-full border rounded-md p-2"
            placeholder="Zone label"
            value={slotForm.zone}
            onChange={(e) => setSlotForm({ ...slotForm, zone: e.target.value })}
          />
          <input
            className="w-full border rounded-md p-2"
            placeholder="Level"
            value={slotForm.parkingLevel}
            onChange={(e) =>
              setSlotForm({ ...slotForm, parkingLevel: e.target.value })
            }
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-full bg-gray-200"
              onClick={() => setSlotModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-full bg-[#0fa9a6] text-white"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!assignModal}
        onClose={() => setAssignModal(null)}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg"
      >
        <form onSubmit={submitAssign} className="p-6 space-y-3">
          <h2 className="text-lg font-semibold">
            Assign slot {assignModal?.slotNumber}
          </h2>
          <select
            required
            className="w-full border rounded-md p-2"
            value={assignVehicleId}
            onChange={(e) => setAssignVehicleId(e.target.value)}
          >
            <option value="">Select vehicle</option>
            {vehicles.map((v) => (
              <option key={v._id || v.id} value={String(v._id || v.id)}>
                {v.vehicleNumber} — {v.ownerName}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500">
            Vehicle list from current Vehicles tab query; widen search if
            missing.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-full bg-gray-200"
              onClick={() => setAssignModal(null)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-full bg-[#0fa9a6] text-white"
            >
              Assign
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={breachModal}
        onClose={() => setBreachModal(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-lg rounded-xl shadow-lg"
      >
        <form onSubmit={submitBreach} className="p-6 space-y-3">
          <h2 className="text-lg font-semibold">Report boundary breach</h2>
          <input
            required
            className="w-full border rounded-md p-2"
            value={breachForm.breachType}
            onChange={(e) =>
              setBreachForm({ ...breachForm, breachType: e.target.value })
            }
          />
          <input
            required
            className="w-full border rounded-md p-2"
            placeholder="Location *"
            value={breachForm.location}
            onChange={(e) =>
              setBreachForm({ ...breachForm, location: e.target.value })
            }
          />
          <textarea
            className="w-full border rounded-md p-2"
            rows={3}
            placeholder="Description"
            value={breachForm.description}
            onChange={(e) =>
              setBreachForm({ ...breachForm, description: e.target.value })
            }
          />
          <select
            className="w-full border rounded-md p-2"
            value={breachForm.severity}
            onChange={(e) =>
              setBreachForm({ ...breachForm, severity: e.target.value })
            }
          >
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-full bg-gray-200"
              onClick={() => setBreachModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-full bg-red-600 text-white"
            >
              Submit
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedVehicle(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <VehicleForm
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsCreateModalOpen(false);
              setSelectedVehicle(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full"
            onClick={() => {
              setIsCreateModalOpen(false);
              setSelectedVehicle(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="vehicleForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full"
          >
            Register
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedVehicle(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <VehicleForm
            vehicle={selectedVehicle || undefined}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedVehicle(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedVehicle(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="vehicleForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full"
          >
            Update
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ParkingManagement;
